import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBoardStore, BoardJournal, BoardChannel } from '../../../stores/boardStore';
import { useAuthStore } from '../../../stores/authStore';
import { PortalDropdown } from '../../../components/PortalDropdown';  // Import PortalDropdown

import { boardService } from '../../../lib/api/boards';
import { channelsService } from '../../../lib/api/channels';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { useUserChannelsStore } from '../../../stores/userChannelsStore';
import { SafeImage } from '../../../components/common/SafeImage';


interface BoardJournalListProps {
    boardId: string;
    onAddJournal?: () => void;
    onOpenEntitySelection?: () => void;
    onConnectChannel?: () => void;
    onSelectChannel?: (channel?: BoardChannel) => void;
    isFinanceLoading?: boolean;
    isFinanceError?: boolean;
    standalone?: boolean;
    journals?: any[];
    onSelectJournal?: (journal: any) => void;
    onDeleteJournal?: (id: string) => void;
    onShareJournal?: (journal: any) => void;
    onRemoveSharedAccess?: (id: string) => void;
    isSharedView?: boolean;
    hideHeaderAction?: boolean;
}

export const BoardJournalList: React.FC<BoardJournalListProps> = ({
    boardId,
    onAddJournal,
    onOpenEntitySelection,
    onConnectChannel,
    onSelectChannel,
    isFinanceLoading,
    isFinanceError,
    standalone = false,
    journals: propJournals = [],
    onSelectJournal,
    onDeleteJournal,
    onShareJournal,
    onRemoveSharedAccess,
    isSharedView = false,
    hideHeaderAction = false
}) => {
    const { t } = useTranslation('common');
    const { journals, deleteJournal, channels, selectedEntities, finance, currencySettings } = useBoardStore();
    const [menuState, setMenuState] = useState<{ id: string | null; anchor: HTMLElement | null }>({ id: null, anchor: null });
    const navigate = useNavigate();


    // Map entity ID to channel ID for calculating financials per channel
    // and aggregate metrics from finance data
    const channelFinancials = useMemo(() => {
        const statsByChannel: Record<string, { expense: number; revenue: number }> = {};

        // Initialize stats for efficient lookup
        channels.forEach(c => {
            statsByChannel[c.id] = { expense: 0, revenue: 0 };
        });

        // Create entity -> channel map
        const entityToChannel: Record<string, string> = {};
        selectedEntities.forEach(e => {
            entityToChannel[e.id] = e.channelId;
        });

        if (finance && finance.breakdown) {
            finance.breakdown.forEach((item: any) => {
                const channelId = entityToChannel[item.entityId];
                if (channelId && statsByChannel[channelId]) {
                    if (item.type === 'EXPENSE') {
                        statsByChannel[channelId].expense += item.value;
                    } else if (item.type === 'REVENUE') {
                        statsByChannel[channelId].revenue += item.value;
                    }
                }
            });
        }

        return statsByChannel;
    }, [finance, channels, selectedEntities, currencySettings]); // Added currencySettings as dependency

    // Fetch supported channels for empty state
    const { data: channelTypesData } = useQuery({
        queryKey: ["channelTypes"],
        queryFn: channelsService.getChannelTypes,
        staleTime: 1000 * 60 * 60, // 1 hour
    });
    const supportedChannels = channelTypesData?.data || [];

    // Use global user channels store instead of fetching
    const { channels: userConnectedChannels, loadChannels } = useUserChannelsStore();

    // Ensure channels are loaded (will use cache if available)
    useEffect(() => {
        loadChannels();
    }, [loadChannels]);

    // Filter unique connected channels by type to avoid duplicates in display
    // We want to show one icon per channel type (e.g. one Shopify icon even if 2 stores connected)
    const uniqueUserChannels = useMemo(() => {
        if (!userConnectedChannels || userConnectedChannels.length === 0) return [];
        const unique = new Map();
        userConnectedChannels.forEach((channel: any) => {
            // prioritize custom icon -> config icon -> nothing
            if (!unique.has(channel.channelType)) {
                unique.set(channel.channelType, channel);
            }
        });
        return Array.from(unique.values());
    }, [userConnectedChannels]);

    const [confirmationModal, setConfirmationModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
    });

    const handleDelete = async (journalId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setMenuState({ id: null, anchor: null });

        setConfirmationModal({
            isOpen: true,
            title: (standalone ? t('journals.deleteJournal') : t('boardJournals.list.deleteJournal')) || 'Delete',
            message: (standalone ? t('journals.confirmDelete') : t('boardJournals.list.confirmDelete')) || 'Are you sure?',
            onConfirm: async () => {
                if (isDeleting) return;
                setIsDeleting(true);
                try {
                    if (standalone) {
                        await boardService.deleteGlobalJournal(journalId);
                        onDeleteJournal?.(journalId);
                    } else {
                        await boardService.deleteJournal(boardId, journalId);
                        deleteJournal(journalId);
                    }
                } catch (error) {
                    console.error('Failed to delete', error);
                } finally {
                    setIsDeleting(false);
                    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
                }
            }
        });
    };

    const handleDuplicate = async (journal: BoardJournal, e: React.MouseEvent) => {
        e.stopPropagation();
        setMenuState({ id: null, anchor: null });
        const newName = `${journal.name} (${t('common.copy')})`;
        try {
            await boardService.createJournal(boardId, {
                name: newName,
                importType: 'none', // Assuming simple duplicate is manual default
            });
            // Add to store (would happen via refetch usually, but optimistic add)
            // We'd probably need addJournal exposed if we want instant update without refetch
            // assuming boardService returns the new journal
            // location.reload(); // Simple refresh for now or let react-query invalidate
        } catch (error) {
            console.error('Failed to duplicate journal', error);
        }
    };

    const toggleMenu = (id: string, e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        if (menuState.id === id) {
            setMenuState({ id: null, anchor: null });
        } else {
            setMenuState({ id: id, anchor: e.currentTarget });
        }
    };

    // Close menu on click outside - Handled by PortalDropdown mostly, but global click close can stay
    // Actually PortalDropdown handles mousedown outside. 
    // We can remove this effect or sync it?
    // PortalDropdown calls onClose when clicking outside. 
    // We should rely on that property.
    // So removing the global click listener here as it might conflict or be redundant.
    /* 
    React.useEffect(() => {
        const handleClickOutside = () => setOpenMenuId(null);
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []); 
    */

    const handleNavigate = (journal: BoardJournal) => {
        if (menuState.id) return; // Don't navigate if menu is open
        if (standalone) {
            onSelectJournal?.(journal);
        } else {
            navigate(`/boards/${boardId}/journals/${journal.id}`);
        }
    };

    const displayJournals = standalone ? propJournals : journals;
    const sortedJournals = [...displayJournals].sort((a, b) => (a.order || 0) - (b.order || 0));
    const visibleJournals = sortedJournals.filter(c => c.type !== 'system');
    const hasJournals = visibleJournals.length > 0;

    // Group selected entities by channel
    const channelGroups = useMemo(() => {
        return channels.map(channel => {
            const entities = selectedEntities.filter(e => e.channelId === channel.id);
            if (entities.length === 0) return null;
            return {
                channel,
                entities
            };
        }).filter(Boolean);
    }, [channels, selectedEntities]);

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isDeleting, setIsDeleting] = useState(false);

    // Reset selection when journals change or filter changes
    useEffect(() => {
        setSelectedIds(new Set());
    }, [boardId, standalone, isSharedView]);

    const handleToggleSelect = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    };

    const handleSelectAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (selectedIds.size === visibleJournals.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(visibleJournals.map(c => c.id)));
        }
    };

    const handleBulkDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (selectedIds.size === 0 || isDeleting) return;

        setConfirmationModal({
            isOpen: true,
            title: (standalone ? t('journals.deleteJournals', 'Delete Journals') : t('boardJournals.list.deleteJournals', 'Delete Journals')),
            message: (standalone ? t('journals.confirmDeleteBulk', `Are you sure you want to delete ${selectedIds.size} journals?`) : t('boardJournals.list.confirmDeleteBulk', `Are you sure you want to delete ${selectedIds.size} journals?`)),
            onConfirm: async () => {
                if (isDeleting) return;
                setIsDeleting(true);
                const idsToDelete = Array.from(selectedIds);
                try {
                    // Use allSettled to prevent one failure from stopping others and to swallow 404s if item already deleted
                    await Promise.allSettled(idsToDelete.map(async (id) => {
                        try {
                            if (standalone) {
                                await boardService.deleteGlobalJournal(id);
                                onDeleteJournal?.(id);
                            } else {
                                await boardService.deleteJournal(boardId, id);
                                deleteJournal(id);
                            }
                        } catch (err: any) {
                            // If it's a 404, we can ignore it as it's already deleted
                            if (err?.message?.includes('not found') || err?.response?.status === 404) {
                                if (standalone) onDeleteJournal?.(id);
                                else deleteJournal(id);
                                return;
                            }
                            throw err;
                        }
                    }));
                    setSelectedIds(new Set());
                } catch (error) {
                    console.error('Failed to bulk delete', error);
                } finally {
                    setIsDeleting(false);
                    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
                }
            }
        });
    };

    return (
        <div className={`flex flex-col w-full h-full relative overflow-hidden ${standalone ? '' : 'bg-white dark:bg-neutral-900'}`}>
            {/* Connections Header - Horizontal Scrollable Space */}
            {!standalone && (
                <div className="w-full border-b border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-sm z-10">
                    <div className="flex items-center gap-4 px-6 py-3 overflow-x-auto scrollbar-hide">
                        {/* Add Selection / Connect Button */}
                        <button
                            onClick={() => {
                                if (channels.length === 0) {
                                    // Empty state or Connected-but-no-selection state
                                    if (userConnectedChannels && userConnectedChannels.length > 0) {
                                        onSelectChannel?.();
                                    } else {
                                        onConnectChannel?.();
                                    }
                                } else {
                                    onOpenEntitySelection?.();
                                }
                            }}
                            className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full border border-dashed border-neutral-300 dark:border-neutral-700 text-neutral-400 dark:text-neutral-500 hover:text-accent-primary hover:border-accent-primary hover:bg-accent-primary/5 dark:hover:bg-accent-primary/10 transition-all"
                            title={
                                channels.length === 0
                                    ? (userConnectedChannels && userConnectedChannels.length > 0 ? (t('channels.selectChannel') || "Select Channel") : (t('channels.connectChannel') || "Connect Channel"))
                                    : (t('actions.addSelection') || "Add Selection")
                            }
                        >
                            {channels.length === 0 ? (
                                userConnectedChannels && userConnectedChannels.length > 0 ? (
                                    // Case 2: User has connections but board is empty -> Icon: Database (Use existing)
                                    <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_68e1afcf.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_68e1afcf.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                ) : (
                                    // Case 1: Totally empty -> Icon: Connect/Plug
                                    <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_bd3d150a.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_bd3d150a.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                )
                            ) : (
                                // Case 3: Has selections -> Icon: Plus
                                <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_db0168eb.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_db0168eb.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            )}
                        </button>

                        {/* Channel Groups */}
                        {channelGroups.map((group) => {
                            if (!group) return null;

                            const stats = channelFinancials[group.channel.id];
                            const { user } = useAuthStore.getState();
                            const currencyCode = useBoardStore.getState().currencySettings?.default || user?.currency || '';
                            const getCurrencySymbol = (code: string) => {
                                try {
                                    return (0).toLocaleString('en-US', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[\d\s.,]/g, '');
                                } catch {
                                    return '$'; // Fallback
                                }
                            }
                            const symbol = getCurrencySymbol(currencyCode);

                            // Format large numbers
                            const formatValue = (val: number) => {
                                if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
                                if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
                                return val.toFixed(0);
                            };

                            return (
                                <div
                                    key={group.channel.id}
                                    className="flex-shrink-0 flex items-center bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-full pl-1.5 pr-3 py-1.5 gap-3 shadow-sm select-none hover:border-accent-primary/30 transition-all cursor-pointer hover:shadow-md"

                                    onClick={() => onSelectChannel?.(group.channel)}
                                >
                                    {/* Channel Icon/Avatar */}
                                    <div className="w-7 h-7 flex items-center justify-center bg-white dark:bg-neutral-800 rounded-full border border-neutral-100 dark:border-neutral-700 text-base overflow-hidden">
                                        {(() => {
                                            const channel = group.channel;
                                            // 1. Channel specific icon (URL)
                                            if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
                                                return (
                                                    <SafeImage
                                                        src={channel.icon}
                                                        alt={channel.name}
                                                        className="w-5 h-5 object-contain"
                                                        fallback={
                                                            channel.channelTypeConfig?.icon ? (
                                                                <SafeImage src={channel.channelTypeConfig.icon} className="w-5 h-5 object-contain" />
                                                            ) : undefined
                                                        }
                                                    />

                                                );
                                            }

                                            // 2. Platform/Type specific icon (URL) - This is key for integrations like Shopify
                                            if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
                                                return <SafeImage src={channel.channelTypeConfig.icon} alt={channel.name} className="w-5 h-5 object-contain" />;

                                            }

                                            // 3. Text/Emoji icon
                                            if (channel.icon && !channel.icon.startsWith('http') && !channel.icon.startsWith('/')) {
                                                return <span className="text-sm shrink-0 leading-none">{channel.icon}</span>;
                                            }

                                            // 4. Fallback to Initials
                                            return (
                                                <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase">{channel.name.substring(0, 1)}</span>
                                            );
                                        })()}
                                    </div>

                                    {/* Channel Info */}
                                    <div className="flex flex-col justify-center min-w-0 max-w-[120px]">
                                        <span className="text-[10px] font-bold text-neutral-600 dark:text-neutral-300 leading-none mb-0.5 truncate">
                                            {group.channel.name}
                                        </span>
                                        <span className="text-[10px] text-neutral-400 font-medium truncate">
                                            {group.entities.length} {t('common.selected', 'selected')}
                                        </span>
                                    </div>

                                    {/* Financial Stats */}
                                    {isFinanceLoading ? (
                                        <div className="flex flex-col items-start justify-center gap-1.5 min-w-[40px]">
                                            <div className="w-10 h-2.5 bg-neutral-100 rounded animate-pulse" />
                                            <div className="w-8 h-2.5 bg-neutral-100 rounded animate-pulse" />
                                        </div>
                                    ) : isFinanceError ? (
                                        <div className="flex flex-col items-start justify-center gap-0.5 min-w-[40px] opacity-50">
                                            <div className="flex items-center gap-0.5">
                                                <span className="w-2 h-2 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_35ff4a28.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_35ff4a28.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                <span className="text-[9px] font-bold text-neutral-400 leading-none">?</span>
                                            </div>
                                            <div className="flex items-center gap-0.5">
                                                <span className="w-2 h-2 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_b573de73.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_b573de73.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                <span className="text-[9px] font-bold text-neutral-400 leading-none">?</span>
                                            </div>
                                        </div>
                                    ) : stats && (
                                        <div className="flex flex-col items-start justify-center gap-0.5 min-w-[40px]">
                                            {/* Revenue */}
                                            <div className="flex items-center gap-0.5" title={`${t('common.revenue')}: ${symbol}${(stats.revenue || 0).toLocaleString()}`}>
                                                <span className="w-2 h-2 text-emerald-500" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_35ff4a28.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_35ff4a28.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                <span className="text-[9px] font-bold text-emerald-600 leading-none">
                                                    {symbol}{formatValue(stats.revenue || 0)}
                                                </span>
                                            </div>

                                            {/* Expense */}
                                            <div className="flex items-center gap-0.5" title={`${t('common.expenses')}: ${symbol}${(stats.expense || 0).toLocaleString()}`}>
                                                <span className="w-2 h-2 text-rose-500" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_b573de73.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_b573de73.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                <span className="text-[9px] font-bold text-rose-600 leading-none">
                                                    {symbol}{formatValue(stats.expense || 0)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Empty State: Supported Channels - Only show if NO connections on board AND NO connections on website */}
                        {channels.length === 0 && userConnectedChannels && userConnectedChannels.length === 0 && supportedChannels.map((channel: any) => (
                            <div
                                key={channel.id}
                                className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full border border-dashed border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all select-none cursor-pointer"
                                title={channel.name}
                                onClick={onConnectChannel}
                            >
                                {channel.icon && (channel.icon.startsWith('http') || channel.icon.startsWith('/')) ? (
                                    <SafeImage src={channel.icon} alt={channel.name} className="w-5 h-5 object-contain" />

                                ) : (
                                    <span className="text-sm">{channel.icon || channel.name.charAt(0)}</span>
                                )}
                            </div>
                        ))}

                        {/* Empty State 2: User Connected Channels - Only show if NO connections on board BUT user HAS connections */}
                        {channels.length === 0 && uniqueUserChannels && uniqueUserChannels.length > 0 && uniqueUserChannels.map((channel: any) => (
                            <div
                                key={channel.id}
                                className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full border border-dashed border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:border-accent-primary dark:hover:border-accent-primary hover:shadow-sm transition-all select-none cursor-pointer group"
                                title={channel.name}
                                onClick={() => onSelectChannel?.()}
                            >
                                {/* Same robust icon logic as main pills */}
                                {(() => {
                                    if (channel.icon && (channel.icon.startsWith("http") || channel.icon.startsWith("/"))) {
                                        return (
                                            <SafeImage
                                                src={channel.icon}
                                                alt={channel.name}
                                                className="w-5 h-5 object-contain opacity-70 group-hover:opacity-100 transition-opacity"
                                                fallback={
                                                    channel.channelTypeConfig?.icon ? (
                                                        <SafeImage src={channel.channelTypeConfig.icon} className="w-5 h-5 object-contain opacity-70 group-hover:opacity-100 transition-opacity" />
                                                    ) : undefined
                                                }
                                            />
                                        );
                                    }
                                    if (channel.channelTypeConfig?.icon && (channel.channelTypeConfig.icon.startsWith("/") || channel.channelTypeConfig.icon.startsWith("http"))) {
                                        return <SafeImage src={channel.channelTypeConfig.icon} alt={channel.name} className="w-5 h-5 object-contain opacity-70 group-hover:opacity-100 transition-opacity" />;

                                    }
                                    if (channel.icon && !channel.icon.startsWith('http') && !channel.icon.startsWith('/')) {
                                        return <span className="text-sm shrink-0 leading-none">{channel.icon}</span>;
                                    }
                                    return (
                                        <span className="text-xs font-bold text-neutral-500 uppercase">{channel.name.substring(0, 1)}</span>
                                    );
                                })()}
                            </div>
                        ))}

                        {/* Empty State Help Text if no selections */}
                        {channelGroups.length === 0 && (
                            <span className="text-sm text-neutral-400 italic">
                                {channels.length === 0
                                    ? (userConnectedChannels && userConnectedChannels.length > 0
                                        ? (t('boards.noConnectionsSelect') || "Select data from your connected accounts.")
                                        : (t('boards.noConnectionsConnect') || "Connect a platform to start tracking your data."))
                                    : (t('boards.noConnections') || "No connections yet. Click + to add sources.")
                                }
                            </span>
                        )}
                    </div>
                </div>
            )}


            {/* Header with Add Button - Desktop only, matching other pages */}
            {(hasJournals || standalone) && onAddJournal && !hideHeaderAction && (
                <div className="hidden lg:flex items-center justify-end mb-4">
                    <button
                        onClick={onAddJournal}
                        className="px-4 py-2 text-xs font-bold bg-accent-primary text-white rounded-xl hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95"

                    >
                        {standalone ? (t('boardJournals.addJournal') || 'Add Journal') : (t('boardJournals.createJournal') || 'Create Journal')}
                    </button>
                </div>
            )}

            {/* Main Content Area */}
            <div className={`flex-1 relative ${!hasJournals ? 'flex items-center justify-center' : 'overflow-y-auto pb-6 scroll-smooth'}`}>
                {/* Empty State */}
                {!hasJournals && (
                    <div className="flex-1 flex flex-col items-center justify-center w-full">
                        <div className="text-center">
                            <div className="w-12 h-12 bg-neutral-100 dark:bg-neutral-800 rounded-xl flex items-center justify-center mx-auto mb-3">

                                {isSharedView ? (
                                    <span className="w-6 h-6 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_6293ed03.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_6293ed03.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                ) : (
                                    <span className="w-6 h-6 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_31fe8b51.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_31fe8b51.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                )}
                            </div>
                            <h3 className="text-sm font-medium text-text-primary dark:text-neutral-200 mb-1">
                                {isSharedView
                                    ? (t('journals.noSharedJournals') || 'No shared journals')
                                    : standalone
                                        ? (t('journals.emptyTitle') || 'No journals yet')
                                        : (t('boardJournals.emptyTitle') || 'No journals yet')}
                            </h3>
                            <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 font-light max-w-xs mx-auto">
                                {isSharedView
                                    ? (t('journals.noSharedJournalsDescription') || 'When someone shares a journal with you, it will appear here.')
                                    : standalone
                                        ? (t('journals.emptyDescription') || 'Journals are global journals of financial operations.')
                                        : (t('boardJournals.emptyDescription') || 'Journals hold financial operations like one-time or recurring records.')}
                            </p>
                            {!isSharedView && onAddJournal && (
                                <button
                                    id="onboarding-add-journal"
                                    onClick={onAddJournal}
                                    className="h-7 px-3 text-xs font-bold bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95"

                                >
                                    {t('boardJournals.createJournal') || 'Create a journal'}
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Journals List - Card Style */}
                {hasJournals && (
                <div className={standalone ? "bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm" : "mt-2"}>


                        {/* Table Header */}
                        <div className={`hidden md:flex items-center py-1.5 ${standalone ? 'px-3' : 'px-6'} border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50`}>
                            <div className="w-[45%] min-w-[200px]">
                                <span className="text-[11px] font-medium text-neutral-500 uppercase tracking-wide">
                                    {t('boardJournals.list.headers.name')}
                                </span>
                            </div>
                            {standalone && (
                                <div className="w-24 md:w-32">
                                    <span className="text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                                        {t('common.created', 'Created')}
                                    </span>
                                </div>
                            )}
                            <div className="flex-1"></div>
                            {/* Checkbox Header Column */}
                            <div className="flex-shrink-0 w-24 flex items-center justify-end gap-2">
                                {/* Delete Button - Conditional */}
                                {selectedIds.size > 0 && (
                                    <button
                                        onClick={handleBulkDelete}
                                        className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-950 rounded transition-colors animate-in fade-in"
                                        title={t('common.delete') || 'Delete'}
                                    >
                                        <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_479ebfe0.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_479ebfe0.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </button>
                                )}
                                <div
                                    onClick={handleSelectAll}
                                    className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-colors ${selectedIds.size > 0 ? 'bg-accent-primary border-accent-primary' : 'bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 hover:border-neutral-400 dark:hover:border-neutral-500'}`}
                                >
                                    {selectedIds.size > 0 && (
                                        <span className="w-3 h-3 text-white" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_cb67a53b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_cb67a53b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* List Items */}
                        <div className={standalone ? "divide-y divide-neutral-100 dark:divide-neutral-800" : "space-y-1"}>
                            {visibleJournals.map(journal => (
                                <div
                                    key={journal.id}
                                    className={`
                                    group flex items-center py-2 ${standalone ? 'px-3' : 'px-6'} hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors cursor-pointer
                                    ${journal.isEnabled ? 'bg-white dark:bg-neutral-900' : 'bg-neutral-50/50 dark:bg-neutral-800/30 opacity-60 hover:opacity-100'}
                                    ${selectedIds.has(journal.id) ? 'bg-accent-primary/5' : ''}
                                `}
                                    onClick={() => handleNavigate(journal)}
                                >
                                    {/* Name & Icon Column */}
                                    <div className="w-[45%] min-w-[200px] flex items-center gap-3">
                                        <div className={`flex items-center justify-center flex-shrink-0 transition-all duration-200 ${standalone
                                            ? 'w-7 h-7 bg-gradient-to-br from-accent-primary to-accent-primary/70 rounded-xl text-white shadow-sm'
                                            : 'w-8 h-8 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400 group-hover:bg-white dark:group-hover:bg-neutral-700 group-hover:text-accent-primary group-hover:shadow-sm border border-transparent group-hover:border-neutral-100 dark:group-hover:border-neutral-600'

                                            }`}>
                                            {standalone ? (
                                                isSharedView ? (
                                                    <div className="relative w-full h-full flex items-center justify-center">
                                                        <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_72df3b1f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_72df3b1f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        {/* Badge background */}
                                                        <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-[1px]">
                                                            {/* User Icon Badge */}
                                                            <div className="w-3.5 h-3.5 bg-accent-primary rounded-full flex items-center justify-center border border-white">
                                                                <span className="w-2 h-2 text-white" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_88a3421c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_88a3421c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4f9e646c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4f9e646c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                )
                                            ) : (
                                                (() => {
                                                    const CDN_URL = (((window as any).__DASHBAL_CONFIG__?.cdnUrl) || 'https://cdn.dashbal.com').replace(/\/$/, '');
                                                    return (
                                                        <SafeImage src={`${CDN_URL}/logos/internal/money_journal.svg`} className="w-4 h-4 object-contain" />
                                                    );
                                                })()
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-xs font-normal text-text-primary dark:text-neutral-200 truncate">
                                                {journal.name}
                                            </h4>
                                            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate hidden md:block">
                                                {isSharedView && (journal as any).owner ? (
                                                    <span className="flex items-center gap-1">
                                                        <span className="w-3 h-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_139da0d8.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_139da0d8.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        {(journal as any).owner.name}
                                                    </span>
                                                ) : (
                                                    journal.type === 'system' ? t('boardJournals.types.automated') : (standalone ? t('journals.journal') : t('boardJournals.journal'))
                                                )}
                                            </p>
                                        </div>
                                    </div>

                                    {standalone && (
                                        <div className="w-24 md:w-32 text-xs text-neutral-500 dark:text-neutral-400 truncate">
                                            {(() => {
                                                const date = (journal as any).createdAt || (journal as any).sharedAt;
                                                if (!date) return '-';
                                                try {
                                                    return new Date(date).toLocaleDateString(undefined, {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric'
                                                    });
                                                } catch (e) {
                                                    return '-';
                                                }
                                            })()}
                                        </div>
                                    )}

                                    <div className="flex-1"></div>

                                    {/* Actions Column (Rightmost) - Menu + Checkbox */}
                                    <div className="flex-shrink-0 w-24 flex items-center justify-end gap-2 relative">
                                        {standalone && !isSharedView && onShareJournal ? (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onShareJournal(journal);
                                                }}
                                                className="p-1.5 text-neutral-400 dark:text-neutral-500 hover:text-accent-primary hover:bg-accent-primary/10 rounded transition-colors"
                                                title={t('sharing.share', 'Share') as string}
                                            >
                                                <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_2053dce0.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_2053dce0.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            </button>
                                        ) : (
                                            <button
                                                onClick={(e) => toggleMenu(journal.id, e)}
                                                className="p-1.5 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors focus:opacity-100"
                                            >
                                                <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_2ffcbd6a.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_2ffcbd6a.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                            </button>
                                        )}

                                        {/* Row Checkbox */}
                                        <div
                                            onClick={(e) => handleToggleSelect(journal.id, e)}
                                            className={`
                                            w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-colors
                                            ${selectedIds.has(journal.id)
                                                    ? 'bg-accent-primary border-accent-primary'
                                                    : 'bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 hover:border-neutral-400 dark:hover:border-neutral-500 text-transparent'
                                                }
                                        `}>
                                            <span className="w-3 h-3 text-white" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_d9119b80.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_d9119b80.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        </div>

                                        {/* Portal Dropdown Menu */}
                                        {!(standalone && !isSharedView && onShareJournal) && (
                                            <PortalDropdown
                                                isOpen={menuState.id === journal.id}
                                                onClose={() => setMenuState({ id: null, anchor: null })}
                                                triggerRef={{ current: menuState.anchor }}
                                                className="w-48"
                                            >
                                                {/* Share button - only for owned journals - kept for completeness but unreachable via simplified button */}
                                                {standalone && !isSharedView && onShareJournal && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMenuState({ id: null, anchor: null });
                                                            onShareJournal(journal);
                                                        }}
                                                        className="w-full text-left px-4 py-2 text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors flex items-center"
                                                    >
                                                        <span className="w-3.5 h-3.5 mr-2" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_c720c5cc.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_c720c5cc.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        {t('sharing.share', 'Share')}
                                                    </button>
                                                )}
                                                {!standalone && (
                                                    <button
                                                        onClick={(e) => handleDuplicate(journal, e)}
                                                        className="w-full text-left px-4 py-2 text-xs text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors flex items-center"
                                                    >
                                                        <span className="w-3.5 h-3.5 mr-2" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_f59be0d2.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_f59be0d2.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        {t('common.duplicate')}
                                                    </button>
                                                )}
                                                {/* Remove shared access - only for shared journals */}
                                                {standalone && isSharedView && onRemoveSharedAccess && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMenuState({ id: null, anchor: null });
                                                            onRemoveSharedAccess(journal.id);
                                                        }}
                                                        className="w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center"
                                                    >
                                                        <span className="w-3.5 h-3.5 mr-2" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_4e12dcb0.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_4e12dcb0.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        {t('sharing.removeAccess', 'Remove Access')}
                                                    </button>
                                                )}
                                                {/* Delete - only for owned journals */}
                                                {(!standalone || !isSharedView) && (
                                                    <button
                                                        onClick={(e) => handleDelete(journal.id, e)}
                                                        className="w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center"
                                                    >
                                                        <span className="w-3.5 h-3.5 mr-2" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_5be48001.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_5be48001.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        {t('common.delete')}
                                                    </button>
                                                )}
                                            </PortalDropdown>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmationModal.isOpen}
                title={confirmationModal.title}
                message={confirmationModal.message}
                onConfirm={confirmationModal.onConfirm}
                onCancel={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
                variant="danger"
            />
        </div>
    );
};
